<template>
	<Teleport to="body">
		<div v-if="isVisible" class="ddd-overlay">
			<div class="ddd-card">
				<!-- ── Header ───────────────────────────────────────────── -->
				<DriverDebugHeader
					:driver-display-name="driverDisplayName"
					:total-score="debugData?.totalScore"
					:work-time="driverAnnotation?.workTime"
					:work-time-difference="driverAnnotation?.workTimeDifference"
					:rest-days-difference="driverAnnotation?.restDaysDifference"
					:violations="debugData?.violations"
					@close="closeDebugView"
				/>

				<!-- ── Toolbar / Date trigger ────────────────────────────── -->
				<DriverDebugToolbar
					:start-date="appliedStartDate?.toISOString().split('T')[0]"
					:end-date="appliedEndDate?.toISOString().split('T')[0]"
					@range-change="onRangeChange"
					@range-clear="clearDates"
				/>

				<!-- ── Data area ─────────────────────────────────────────── -->
				<div class="ddd-scroll">
					<!-- Loading overlay -->
					<div v-if="isLoading" class="ddd-loading-overlay">
						<v-progress-circular indeterminate color="primary" size="42" />
						<p class="ddd-empty-sub">{{ t('planning_driver_debug_loading_data') }}</p>
					</div>

					<div :class="{ 'ddd-loading-disabled': isLoading }" ref="table-scroller">
						<!-- Data table -->
						<DriverDebugTable
							:days="debugData?.days ?? []"
							:driver-days="driverDays"
							:score-keys="scoreKeys"
							:fitness-data-keys="fitnessDataKeys"
							:fitness-data="debugData?.fitnessData"
							:basic-fitness-data="debugData?.basicFitnessData"
							:met-days="metDays"
							:unmet-days="unmetDays"
							:violations-days="violationsDays"
							:manual-worktime-balances="manualWorktimeBalances"
						/>

						<!-- Summary panels -->
						<DriverDebugPanels
							:score="debugData?.score"
							:objectives="debugData?.objectives"
							:violations="debugData?.violations"
							:driver-annotation="driverAnnotation"
						/>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { useDriverDebugData } from '@/rennovation/planning-view/composables/useDriverDebugData';
import useDriverDebugDatePicker from '@/rennovation/planning-view/composables/useDriverDebugDatePicker';
import { useDriverDebugRange } from '@/rennovation/planning-view/composables/useDriverDebugRange';
import DriverDebugHeader from '@/rennovation/planning-view/components/Presentation/DriverDebugHeader.vue';
import DriverDebugToolbar from '@/rennovation/planning-view/components/Presentation/DriverDebugToolbar.vue';
import DriverDebugTable from '@/rennovation/planning-view/components/Presentation/DriverDebugTable.vue';
import DriverDebugPanels from '@/rennovation/planning-view/components/Presentation/DriverDebugPanels.vue';
import { ApiManualBalance } from '@/rennovation/planning-view/types/api/manual-balance-response';

// ─────────────────────────────────────────────────────────────────────────────
// Props / emits
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{ driverId: string }>();
const emit = defineEmits<{ (e: 'close'): void }>();
const tableScroller = ref<HTMLElement | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// i18n / stores
// ─────────────────────────────────────────────────────────────────────────────
const { t } = useI18n();
const planningStore = usePlanningStore();

const selectedDriver = computed(() => planningStore.drivers?.[props.driverId] ?? null);
const manualBalancesById = shallowRef<Record<string, ApiManualBalance>>({}); // ← keyed by day._id
const manualWorktimeBalances = computed(() => Object.values(manualBalancesById.value) as ApiManualBalance[]);

// ─────────────────────────────────────────────────────────────────────────────
// Composables
// ─────────────────────────────────────────────────────────────────────────────
const debugRange = useDriverDebugRange();

const { debugData, isLoading, scoreKeys, fitnessDataKeys, driverDays, driverAnnotation, driverDisplayName, violationsDays, metDays, unmetDays, loadDebugData } =
	useDriverDebugData(props.driverId, debugRange, {
		selectedDriver,
		manualWorktimeBalances,
	});

const { appliedStartDate, appliedEndDate, clearDates, restoreFromStorage } = useDriverDebugDatePicker(debugRange);

// ─────────────────────────────────────────────────────────────────────────────
// Local state
// ─────────────────────────────────────────────────────────────────────────────
const isVisible = ref(false);

// ─────────────────────────────────────────────────────────────────────────────
// Computed style helpers (kept here to avoid circular prop chains)
// ─────────────────────────────────────────────────────────────────────────────

function onRangeChange(start: string, end: string) {
	appliedStartDate.value = new Date(start + 'T12:00:00');
	appliedEndDate.value = new Date(end + 'T12:00:00');
	// Persist to localStorage the same way applyRange() does
	try {
		localStorage.setItem('debugStartDate', String(appliedStartDate.value.getTime()));
		localStorage.setItem('debugEndDate', String(appliedEndDate.value.getTime()));
	} catch {
		/* ignore */
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// UI handlers
// ─────────────────────────────────────────────────────────────────────────────
function closeDebugView() {
	isVisible.value = false;
	emit('close');
}

const adjustWidth = () => {
	setTimeout(() => {
		const tableElement = document.querySelector('.ddd-table');

		if (tableElement && tableScroller.value) {
			tableScroller.value.style.width = `${tableElement.scrollWidth}px`;
		}
	}, 1000);
};

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle
// ─────────────────────────────────────────────────────────────────────────────
onMounted(async () => {
	isVisible.value = true;
	restoreFromStorage();
	await planningStore.loadManualWorktimeBalances(props.driverId);
	adjustWidth();
});

onBeforeUnmount(() => {
	clearDates();
});
watch(isLoading, (loading) => {
	if (!loading) adjustWidth();
});
watch(
	[appliedStartDate, appliedEndDate, () => props.driverId],
	async ([start, end, driverId]) => {
		if (!start || !end || !driverId) return;
		await loadDebugData();
	},
	{ immediate: true }
);
</script>

<style scoped>
/* ─────────────────────────────────────────────────────────────────────────────
   SINGLE SOURCE OF TRUTH: all CSS variables live here on .ddd-overlay.
   Child components inherit these via the cascade — never duplicate them.
───────────────────────────────────────────────────────────────────────────── */
.ddd-overlay {
	/* ── Fonts & layout ─────────────────────────────────────────── */
	--ddd-font-mono: 'Roboto Mono', 'Courier New', monospace;
	--ddd-font-ui: system-ui, -apple-system, sans-serif;
	--ddd-label-w: 200px;
	--ddd-cell-min: 130px;
	--ddd-header-h: 44px;
	--ddd-toolbar-h: 42px;
	--ddd-row0-h: 44px;

	/* ── Structural / background ────────────────────────────────── */
	--ddd-border: #d1d9e0;
	--ddd-border-row: #e8ecf0;
	--ddd-bg-card: #ffffff;
	--ddd-bg-head: #f0f4f8;
	--ddd-bg-toolbar: #fafcfe;
	--ddd-bg-section: #e4eaf0;
	--ddd-bg-total-cell: #f0f4f8;
	--ddd-bg-panel-header: #f0f4f8;
	--ddd-drop-border: #f0f0f0;

	/* ── Text ───────────────────────────────────────────────────── */
	--ddd-text-primary: #0a0a0a;
	--ddd-text-dark: #0a1520;
	--ddd-text-sub: #1a2e40;
	--ddd-text-mid: #2d4560;
	--ddd-text-muted: #3a5068;
	--ddd-text-tertiary: #8a9bb0;
	--ddd-text-empty-icon: #b0bec5;

	/* ── Avatar ─────────────────────────────────────────────────── */
	--ddd-avatar-bg: #1a4a7a;

	/* ── Pills ──────────────────────────────────────────────────── */
	--ddd-pill-blue-bg: #dbeafe;
	--ddd-pill-blue-border: #93c5fd;
	--ddd-pill-green-bg: #dcfce7;
	--ddd-pill-green-border: #86efac;
	--ddd-pill-teal-bg: #ccfbf1;
	--ddd-pill-teal-border: #99f6e4;
	--ddd-pill-label: #0f2a4a;
	--ddd-pill-value: #0a1520;
	--ddd-pill-sub: #1a3a5a;

	/* ── Close button ───────────────────────────────────────────── */
	--ddd-close-hover-bg: #fee2e2;
	--ddd-close-hover-text: #b91c1c;
	--ddd-close-hover-border: #fca5a5;

	/* ── Scrollbar ──────────────────────────────────────────────── */
	--ddd-scrollbar-thumb: #bfccd8;
	--ddd-scrollbar-hover: #94a8b8;

	/* ── Sticky column shadow ───────────────────────────────────── */
	--ddd-sticky-shadow: #c5d0db;

	/* ── Semantic label / stat colors ───────────────────────────── */
	--ddd-color-met: #0d3a1c;
	--ddd-color-unmet: #3a2800;
	--ddd-color-viol: #5a0d0d;

	/* ── Day status ─────────────────────────────────────────────── */
	--ddd-status-open: #9e9e9e;
	--ddd-status-planning: #90caf9;
	--ddd-status-published: #1976d2;
	--ddd-status-locked: #0288d1;
	--ddd-status-preview: #00bfa5;

	/* ── Shift colors ───────────────────────────────────────────── */
	--ddd-shift-1: #3498db;
	--ddd-shift-2: #2ecc71;
	--ddd-shift-3: #f1c40f;
	--ddd-shift-4: #9b59b6;

	/* ── Duty type colors ───────────────────────────────────────── */
	--ddd-duty-duty: #bdc3c7;
	--ddd-duty-reserve: #b8c9d4;
	--ddd-duty-compensation: #3498db;
	--ddd-duty-vacation: #f1c40f;
	--ddd-duty-absence: #e74c3c;
	--ddd-duty-rest: #2ecc71;
	--ddd-duty-rest-sunday: #27ae60;

	/* ── Row highlight colors ───────────────────────────────────── */
	--ddd-standby-bg: #e67e22;
	--ddd-met-bg: #e3f2fd;
	--ddd-unmet-bg: #e1f5fe;
	--ddd-viol-bg: #ffcdd2;
}

/* ── Overlay ─────────────────────────────────────────────────────────────── */
.ddd-overlay {
	position: fixed;
	inset: 0;
	z-index: 6000;
	background: rgba(0, 0, 0, 0.22);
	display: flex;
	justify-content: center;
	align-items: flex-start;
	padding: 18px 14px;
	overflow: auto;
}

/* ── Card ────────────────────────────────────────────────────────────────── */
.ddd-card {
	width: 97%;
	max-width: 1600px;
	min-width: 200px;
	max-height: calc(100vh - 56px);
	min-height: 200px;
	background: var(--ddd-bg-card);
	border-radius: 10px;
	border: 1px solid var(--ddd-border);
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

/* ── Loading ─────────────────────────────────────────────────────────────── */
.ddd-loading-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.6);
	z-index: 5000;
}

.ddd-loading-disabled {
	opacity: 0.35;
	pointer-events: none;
	transition: opacity 0.2s;
}

.ddd-empty-sub {
	font-family: var(--ddd-font-ui);
	font-size: 13px;
	color: var(--ddd-text-mid);
	text-align: center;
	max-width: 300px;
	margin: 0;
	line-height: 1.6;
}

/* ── Scroll container ────────────────────────────────────────────────────── */
.ddd-scroll {
	flex: 1;
	min-height: 0;
	overflow: auto;
	scrollbar-width: thin;
	scrollbar-color: var(--ddd-scrollbar-thumb) transparent;
}

:deep(.ddd-scroll::-webkit-scrollbar) {
	width: 100px !important;
	height: 50px;
}

.ddd-scroll::-webkit-scrollbar-thumb {
	background: var(--ddd-scrollbar-thumb);
	border-radius: 5px;
}

.ddd-scroll::-webkit-scrollbar-thumb:hover {
	background: var(--ddd-scrollbar-hover);
}
</style>
